import ReportMetric from '@app/components/reports/components/report-metric';
import Text from '@shared/components/content/text';
import Unit, { UnitVariant } from '@shared/components/content/unit';
import Loader from '@shared/components/loader';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@shared/components/ui/sheet';
import { cn } from '@shared/lib/utils';
import { ClassValue } from 'clsx';
import { Dispatch, ReactNode } from 'react';

type MetricProp = {
  title: string;
  value: ReactNode;
  unit: UnitVariant;
  titleClassName?: ClassValue;
  valueClassName?: ClassValue;
};

interface PreviewSheetProps {
  title: string | React.ReactNode;
  className?: ClassValue;
  children: React.ReactNode;
  metrics?: MetricProp[];
  status: 'loading' | 'error' | 'success';
  isOpen: boolean;
  setIsOpen: Dispatch<React.SetStateAction<boolean>>;
  tags?: string[];
  metricsTitle?: string[];
}

interface MetricProps {
  title?: string;
  value: string | number;
  unit?: UnitVariant;
  className?: ClassValue;
}

export function Metric({ title, value, unit, className }: MetricProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-baseline space-x-2">
        <Text
          variant="reportBody"
          className={cn('text-primary-800 text-3xl font-medium')}
        >
          {value}
        </Text>
        <Unit variant={unit!} className="text-md" />
      </div>
      <Text className={cn('')}>{title}</Text>
    </div>
  );
}

export default function PreviewSheet({
  title,
  className,
  children,
  tags,
  status,
  isOpen,
  metrics,
  setIsOpen,
  metricsTitle,
  ...props
}: PreviewSheetProps) {
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button className="bg-teal" size="sm">
          View insights
        </Button>
      </SheetTrigger>
      <SheetContent
        className={cn('space-y-2 overflow-y-auto sm:max-w-lg', className)}
        {...props}
      >
        {status === 'loading' && <Loader />}
        {status === 'error' && (
          <div className="flex h-full justify-center ">
            <Text className="my-auto text-error">Error loading data</Text>
          </div>
        )}
        {status === 'success' && (
          <div className="-mt-3.5 flex h-full flex-col">
            <SheetHeader className="space-y-2 border-b pb-4">
              <SheetTitle className="mt-2 flex flex-row items-center">
                {title}
              </SheetTitle>
            </SheetHeader>

            {tags && tags?.length > 0 && (
              <div className="mt-3">
                <Text variant="subtle" className="mb-2 mr-2 text-xs">
                  Filtered by
                </Text>
                <div className="flex flex-wrap items-center">
                  {tags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="mb-2 mr-1 w-fit"
                      size="sm"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {metricsTitle && metricsTitle?.length > 0 && (
              <div className="mt-6 grid w-full grid-cols-2 gap-4">
                {metricsTitle?.map((title) => {
                  return (
                    <Text key={title} className="text-primary-700">
                      {title}
                    </Text>
                  );
                })}
              </div>
            )}
            <div className="mt-4 flex h-full flex-col space-y-6">
              {metrics && (
                <div className={cn('w-full grid grid-cols-2 gap-4', {})}>
                  {metrics?.map((metric, index) => {
                    return (
                      <ReportMetric
                        key={index}
                        title={metric.title}
                        value={metric.value}
                        unit={metric.unit}
                        size="md"
                        className="w-full"
                        titleClassName={cn(
                          'font-default font-normal text-sm',
                          metric.titleClassName
                        )}
                        valueClassName={cn('text-3xl', metric.valueClassName)}
                      />
                    );
                  })}
                </div>
              )}
              {children}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
